<script setup lang="ts">
import type { SuccessResponseBody } from '#core/composables/useApiFetch'
import type { ConnectInterestType, ConnectTokenStatusType } from '@forgd/contract/types'
import { bindField, revealErrors } from '#core/utils/bind-field'
import LConnectCoinSelect from '#leaderboards/components/LConnectCoinSelect.vue'
import { CONNECT_TOKEN_STATUS_OPTIONS } from '@forgd/contract/types'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Button } from '@forgd/ui-kit/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@forgd/ui-kit/dialog'
import { Input } from '@forgd/ui-kit/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@forgd/ui-kit/select'
import { ToggleGroup, ToggleGroupItem } from '@forgd/ui-kit/toggle-group'
import { CheckCircleIcon } from '@heroicons/vue/24/outline'
import { useMutation } from '@pinia/colada'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import z from 'zod/v4'

const props = defineProps<{
  marketMakerId: string
  marketMakerName: string
}>()

const isOpen = defineModel<boolean>('open', { required: true })

const auth = useAuth()
const toast = useAppToast()

const turnstile = ref()
const turnstileToken = ref<string>()
// signed-in users are already verified — captcha only guards anonymous submissions
const isTurnstileRequired = computed(() => Boolean(useRuntimeConfig().public.turnstileEnabled) && !auth.isLoggedIn)

const tokenStatusLabelId = useId()

const interestOptions = [
  { label: 'Market making for my token', value: 'engage-market-maker' },
  { label: 'Treasury management', value: 'treasury-management' },
  { label: 'Other', value: 'other' },
] as const satisfies { label: string, value: ConnectInterestType }[]

const tokenStatusOptions = [
  { label: 'Pre-TGE', value: 'pre-tge' },
  { label: 'Listed', value: 'listed' },
  { label: 'I don’t have a Token', value: 'no-token' },
] as const satisfies { label: string, value: ConnectTokenStatusType }[]

interface ConnectFormValues {
  // the schema only requires a non-empty string, so the narrow option type is applied on submit
  interest: string
  tokenStatus: ConnectTokenStatusType
  projectName: string
  tokenTicker: string
  fullName: string
  email: string
}

type CoinSearchResult = SuccessResponseBody<'/coins/search'>['coins'][number]

// listed-token search state: either a coin is picked, or the user says it's not in the list
const coinSelection = ref<CoinSearchResult | 'not-in-list' | null>(null)
const selectedCoin = computed(() => (coinSelection.value && coinSelection.value !== 'not-in-list' ? coinSelection.value : null))
const isTokenNotInList = computed(() => coinSelection.value === 'not-in-list')

// pre-fill from the profile and the active project for logged-in users
function buildPrefilledValues(): ConnectFormValues {
  const values: ConnectFormValues = {
    interest: '',
    tokenStatus: 'pre-tge',
    projectName: '',
    tokenTicker: '',
    fullName: '',
    email: '',
  }

  coinSelection.value = null

  if (!auth.isLoggedIn) {
    return values
  }

  if (auth.me) {
    values.fullName = [auth.me.firstName, auth.me.lastName].filter(Boolean).join(' ')
    values.email = auth.me.email ?? ''
  }

  const project = auth.project && !auth.project.isResearch ? auth.project : null
  if (!project) {
    return values
  }

  values.tokenStatus = project.tokenListed ? 'listed' : project.hasToken ? 'pre-tge' : 'no-token'

  if (values.tokenStatus === 'no-token') {
    return values
  }

  values.projectName = project.name
  values.tokenTicker = project.ticker ?? ''

  if (values.tokenStatus === 'listed') {
    coinSelection.value = project.coingeckoId
      ? { id: project.coingeckoId, name: project.name, symbol: project.ticker ?? '', image: project.image ?? '' }
      : 'not-in-list'
  }

  return values
}

const formSchema = z
  .object({
    interest: z.string().min(1, 'Select an option'),
    tokenStatus: z.enum(CONNECT_TOKEN_STATUS_OPTIONS),
    projectName: z.string().trim().max(200, 'Project name is too long'),
    tokenTicker: z.string().trim().max(20, 'Token ticker is too long'),
    fullName: z.string().trim().min(1, 'Full name is required').max(200, 'Full name is too long'),
    email: z.email('Enter a valid email address'),
  })
  .superRefine((value, ctx) => {
    if (value.tokenStatus !== 'no-token') {
      if (!value.projectName.trim()) {
        ctx.addIssue({ code: 'custom', path: ['projectName'], message: 'Project name is required' })
      }
      if (!value.tokenTicker.trim()) {
        ctx.addIssue({ code: 'custom', path: ['tokenTicker'], message: 'Token ticker is required' })
      }
    }
  })

type ConnectFormState = z.infer<typeof formSchema>

const form = useForm({
  defaultValues: buildPrefilledValues(),
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  onSubmitInvalid: ({ formApi }) => revealErrors(formApi),
  validators: { onDynamic: formSchema },
  onSubmit: ({ value }) => submitForm(formSchema.parse(value)),
})

const formValues = form.useSelector(state => state.values)
const isFormValid = form.useSelector(state => formSchema.safeParse(state.values).success)

const hasToken = computed(() => formValues.value.tokenStatus !== 'no-token')
const isListed = computed(() => formValues.value.tokenStatus === 'listed')

const showProjectRow = computed(() =>
  formValues.value.tokenStatus === 'pre-tge' || (isListed.value && isTokenNotInList.value),
)

const canSubmit = computed(() =>
  isFormValid.value
  && (!isTurnstileRequired.value || !!turnstileToken.value),
)

function handleCoinChange(coin: CoinSearchResult) {
  form.setFieldValue('projectName', coin.name)
  form.setFieldValue('tokenTicker', coin.symbol.toUpperCase())
}

function handleTokenNotInList() {
  form.setFieldValue('projectName', '')
  form.setFieldValue('tokenTicker', '')
}

function handleTokenStatusChange(value: unknown) {
  const option = tokenStatusOptions.find(item => item.value === value)
  if (!option) {
    return
  }

  form.setFieldValue('tokenStatus', option.value)
  coinSelection.value = null
  form.setFieldValue('projectName', '')
  form.setFieldValue('tokenTicker', '')
}

function resetForm() {
  form.reset(buildPrefilledValues())
  // turnstile tokens are single-use — the consumed one must not gate the next submission
  turnstileToken.value = undefined
}

const isSubmitted = ref(false)

// reopening the modal should always start from a fresh form, not the last success screen
watch(isOpen, (open) => {
  if (open) {
    isSubmitted.value = false
  }
})

const { mutateAsync: submitConnectRequest, isLoading: isSubmitting } = useMutation(
  buildApiMutation('/market-makers/{marketMakerId}/connect-requests', {
    method: 'post',
  }),
)

async function submitForm(data: ConnectFormState) {
  try {
    await submitConnectRequest({
      path: { marketMakerId: props.marketMakerId },
      body: {
        interest: data.interest as ConnectInterestType,
        tokenStatus: data.tokenStatus as ConnectTokenStatusType,
        coingeckoId: isListed.value && selectedCoin.value ? selectedCoin.value.id : undefined,
        projectName: hasToken.value ? data.projectName.trim() : undefined,
        tokenTicker: hasToken.value ? data.tokenTicker.trim() : undefined,
        fullName: data.fullName,
        email: data.email,
        turnstileToken: turnstileToken.value || undefined,
      },
    })
    isSubmitted.value = true
    resetForm()
  }
  catch {
    // turnstile tokens are single-use — get a fresh one before a retry
    turnstile.value?.reset()

    toast.error({
      title: 'Failed to submit request',
      description: 'Please try again.',
    })
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="(open) => !open && (isOpen = false)">
    <DialogContent class="max-w-[500px]">
      <div v-if="isSubmitted" class="flex flex-col items-center gap-4 pt-4 text-center font-display">
        <CheckCircleIcon class="size-10 text-accent-green" />
        <DialogTitle class="text-xl font-semibold text-primal">
          We have received your request
        </DialogTitle>
        <DialogDescription class="text-sm text-secondary">
          Our team will review the details of your request, and will reach out via the provided contact information shortly with next steps.
        </DialogDescription>
        <Button variant="outline" size="md" class="w-full" @click="isOpen = false">
          Got It
        </Button>
      </div>

      <div v-else class="flex flex-col gap-5 font-display">
        <div class="flex flex-col gap-2.5">
          <DialogTitle class="text-xl font-semibold text-primal">
            Get in touch with {{ props.marketMakerName }}
          </DialogTitle>
          <DialogDescription class="text-sm text-secondary">
            Complete the intake form below, and we’ll put you in direct contact with their team.
          </DialogDescription>
        </div>

        <form novalidate @submit.prevent="form.handleSubmit()">
          <div class="flex flex-col gap-5">
            <form.Field v-slot="{ field }" name="interest">
              <Field>
                <FieldLabel v-bind="bindField(field).label">
                  What are you interested in?
                </FieldLabel>
                <FieldContent>
                  <Select
                    :model-value="bindField(field).control.modelValue"
                    @update:model-value="bindField(field).control['onUpdate:modelValue']"
                  >
                    <SelectTrigger
                      :id="bindField(field).control.id"
                      :error="bindField(field).control.error"
                      :aria-describedby="bindField(field).control['aria-describedby']"
                      data-testid="connectInterest"
                      class="w-full"
                      @blur="field.handleBlur()"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="opt in interestOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FieldError v-bind="bindField(field).error" />
                </FieldContent>
              </Field>
            </form.Field>

            <Field>
              <FieldLabel :id="tokenStatusLabelId">
                What is the status of your token
              </FieldLabel>
              <FieldContent>
                <ToggleGroup
                  preset="light"
                  size="md"
                  layout="fill"
                  :model-value="formValues.tokenStatus"
                  :aria-labelledby="tokenStatusLabelId"
                  @update:model-value="handleTokenStatusChange"
                >
                  <ToggleGroupItem v-for="opt in tokenStatusOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </ToggleGroupItem>
                </ToggleGroup>
              </FieldContent>
            </Field>

            <LConnectCoinSelect
              v-if="isListed"
              v-model="coinSelection"
              class="w-full"
              @change="handleCoinChange"
              @not-in-list="handleTokenNotInList"
            />

            <div v-if="isListed && isTokenNotInList" class="flex items-center gap-4">
              <span class="shrink-0 text-xs text-secondary">Let’s find your token</span>
              <div class="grow border-t border-neutral-600" />
            </div>

            <div v-if="showProjectRow" class="grid gap-5 md:grid-cols-2">
              <form.Field v-slot="{ field }" name="projectName">
                <Field>
                  <FieldLabel v-bind="bindField(field).label">
                    Project Name
                  </FieldLabel>
                  <FieldContent>
                    <Input v-bind="bindField(field).control" :name="field.name" />
                    <FieldError v-bind="bindField(field).error" />
                  </FieldContent>
                </Field>
              </form.Field>
              <form.Field v-slot="{ field }" name="tokenTicker">
                <Field>
                  <FieldLabel v-bind="bindField(field).label">
                    Token Ticker
                  </FieldLabel>
                  <FieldContent>
                    <Input v-bind="bindField(field).control" :name="field.name" />
                    <FieldError v-bind="bindField(field).error" />
                  </FieldContent>
                </Field>
              </form.Field>
            </div>

            <div class="flex items-center gap-4">
              <span class="shrink-0 text-xs text-secondary">Contact Information</span>
              <div class="grow border-t border-neutral-600" />
            </div>

            <div class="grid gap-5 md:grid-cols-2">
              <form.Field v-slot="{ field }" name="fullName">
                <Field>
                  <FieldLabel v-bind="bindField(field).label">
                    Full Name
                  </FieldLabel>
                  <FieldContent>
                    <Input v-bind="bindField(field).control" :name="field.name" />
                    <FieldError v-bind="bindField(field).error" />
                  </FieldContent>
                </Field>
              </form.Field>
              <form.Field v-slot="{ field }" name="email">
                <Field>
                  <FieldLabel v-bind="bindField(field).label">
                    Email
                  </FieldLabel>
                  <FieldContent>
                    <Input v-bind="bindField(field).control" :name="field.name" />
                    <FieldError v-bind="bindField(field).error" />
                  </FieldContent>
                </Field>
              </form.Field>
            </div>

            <NuxtTurnstile v-if="isTurnstileRequired" ref="turnstile" v-model="turnstileToken" />

            <div class="flex gap-4 [&>*]:flex-1">
              <Button variant="outline" size="md" @click="isOpen = false">
                Cancel
              </Button>
              <Button
                type="submit"
                size="md"
                :disabled="!canSubmit"
                :loading="isSubmitting"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </form>
      </div>
    </DialogContent>
  </Dialog>
</template>
